import React from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';

export default function AlertDialog({ 
  isOpen, 
  onClose, 
  title = 'Alert',
  message = '',
  buttonText = 'OK',
  variant = 'info'
}) {
  const isMobile = useIsMobile();

  if (!isOpen) return null;

  const variantIcons = {
    success: (
      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    error: (
      <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    warning: (
      <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    info: (
      <svg className="w-6 h-6 text-kindredly-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const variantColors = {
    success: 'bg-green-50 dark:bg-green-900/20',
    error: 'bg-red-50 dark:bg-red-900/20',
    warning: 'bg-yellow-50 dark:bg-yellow-900/20',
    info: 'bg-blue-50 dark:bg-blue-900/20',
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={onClose}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="alert-title"
    >
      <div className="absolute inset-0 bg-black/50" />
      
      <div 
        className={`relative bg-white dark:bg-gray-800 shadow-2xl transform transition-all ${
          isMobile
            ? 'w-full h-full flex flex-col p-6'
            : 'rounded-xl max-w-md w-full mx-4 p-6'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-4">
          <div className={`flex-shrink-0 p-2 rounded-full ${variantColors[variant]}`}>
            {variantIcons[variant]}
          </div>
          
          <div className={`flex-1 ${isMobile ? 'flex-grow' : ''}`}>
            <h2 
              id="alert-title"
              className="text-xl font-semibold text-gray-900 dark:text-white mb-2"
            >
              {title}
            </h2>
            
            <p className="text-gray-600 dark:text-gray-300">
              {message}
            </p>
          </div>
        </div>

        {isMobile && <div className="flex-1" />}
        
        <div className={isMobile ? '' : 'flex justify-end'}>
          <button
            onClick={onClose}
            className={`px-6 py-3 min-h-[44px] rounded-lg bg-kindredly-blue hover:bg-kindredly-blue-dark text-white transition-colors ${isMobile ? 'w-full' : 'min-w-[100px]'}`}
            autoFocus
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}
